import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, BookUser, Plus, Pencil, Trash2, Search } from "lucide-react";

const RUOLI = { perito: "Perito", liquidatore: "Liquidatore", legale: "Legale" };
const VUOTO = { ruolo: "perito", nome: "", studio: "", telefono: "", email: "", pec: "", note: "" };
const CAMPI = [["nome", "Nome e cognome"], ["studio", "Studio / società"], ["telefono", "Telefono"], ["email", "Email"], ["pec", "PEC"]];

export default function RubricaIncaricati() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [ruolo, setRuolo] = useState("all");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);

  const load = () => api.get("/sinistri/rubrica", { params: { ruolo: ruolo === "all" ? "" : ruolo, q } }).then(({ data }) => setItems(data));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [ruolo, q]); // eslint-disable-line

  const save = async () => {
    try {
      const body = { ...VUOTO, ...dlg };
      if (dlg.id) await api.put(`/sinistri/rubrica/${dlg.id}`, body); else await api.post("/sinistri/rubrica", body);
      toast.success("Rubrica aggiornata"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Eliminare ${r.nome} dalla rubrica? I sinistri già compilati non cambiano.`)) return;
    await api.delete(`/sinistri/rubrica/${r.id}`); toast.success("Eliminato"); load();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate("/gestione-sinistri")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Tutti i sinistri</button>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2"><BookUser className="h-6 w-6 text-red-600" /><h1 className="font-heading text-2xl font-medium text-slate-900">Rubrica incaricati</h1></div>
            <p className="text-slate-500 text-sm mt-1">Periti, liquidatori e legali. Si aggiorna da sola quando li inserisci in un sinistro; le correzioni valgono per i nuovi inserimenti.</p>
          </div>
          <Button onClick={() => setDlg({ ...VUOTO })} className="bg-red-600 hover:bg-red-700 text-white" data-testid="rub-add-btn"><Plus className="h-4 w-4 mr-2" /> Nuovo nominativo</Button>
        </div>
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, studio, email, telefono…" className="pl-9 bg-white" data-testid="rub-search" /></div>
          <Select value={ruolo} onValueChange={setRuolo}><SelectTrigger className="w-44" data-testid="rub-filter-ruolo"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i ruoli</SelectItem>{Object.entries(RUOLI).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Ruolo</TableHead><TableHead>Nome</TableHead><TableHead>Studio</TableHead><TableHead>Telefono</TableHead><TableHead>Email / PEC</TableHead><TableHead className="text-right">Sinistri</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {items.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Nessun nominativo.</TableCell></TableRow>
                : items.map((r) => (
                  <TableRow key={r.id} data-testid={`rub-row-${r.id}`}>
                    <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{RUOLI[r.ruolo]}</span></TableCell>
                    <TableCell className="font-medium text-slate-900">{r.nome}</TableCell>
                    <TableCell className="text-sm">{r.studio || "—"}</TableCell>
                    <TableCell className="text-sm">{r.telefono || "—"}</TableCell>
                    <TableCell className="text-sm">{[r.email, r.pec].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="text-right text-sm">{r.n_sinistri}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setDlg({ ...r })} data-testid={`rub-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(r)} data-testid={`rub-del-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </main>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="rub-dialog">
          <DialogHeader><DialogTitle className="font-heading">{dlg?.id ? "Modifica nominativo" : "Nuovo nominativo"}</DialogTitle>
            <DialogDescription>Le modifiche non cambiano i sinistri già compilati.</DialogDescription></DialogHeader>
          {dlg && (
            <div className="grid grid-cols-2 gap-3 py-1">
              <div className="col-span-2 space-y-1.5"><Label className="text-sm text-slate-600">Ruolo</Label>
                <Select value={dlg.ruolo} onValueChange={(v) => setDlg({ ...dlg, ruolo: v })}><SelectTrigger data-testid="rub-f-ruolo"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(RUOLI).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
              {CAMPI.map(([k, l]) => (
                <div key={k} className="space-y-1.5"><Label className="text-sm text-slate-600">{l}</Label>
                  <Input value={dlg[k] || ""} onChange={(e) => setDlg({ ...dlg, [k]: e.target.value })} data-testid={`rub-f-${k}`} /></div>
              ))}
              <div className="col-span-2 space-y-1.5"><Label className="text-sm text-slate-600">Note</Label>
                <Textarea rows={2} value={dlg.note || ""} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} data-testid="rub-f-note" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button>
            <Button onClick={save} disabled={!dlg?.nome?.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="rub-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
